import React, { useCallback, useEffect } from "react";
import * as Scrivito from "scrivito";
import { useFormikContext } from "formik";
import NiceModal from "@ebay/nice-modal-react";
import { useAppBaseContext, useConfiguratorPopupContext } from "providers";
import { getHomepage } from "utils";
import { Button, ProductAddingModal } from "components";
import { useTranslate } from "hooks";
import { NodeKinds } from "interfaces/enums";

export const CustomProductHeader = Scrivito.connect(
  ({ projectId, customProductId }) => {
    const translate = useTranslate();
    const { values, validateForm } = useFormikContext();
    const { lang } = useAppBaseContext();
    const {
      setParams: updateParams,
      isUpdateButtonActive,
      showConfiguratorPopup,
    } = useConfiguratorPopupContext();

    useEffect(() => {
      updateParams(() => ({
        id: customProductId,
        kind: NodeKinds.CUSTOM_PRODUCT,
        orderCode: null,
        productName: values.title,
        productDescription: values.description,
        priceGroup: values.priceGroup,
        presentation: values.ordercode,
        price: isNaN(values.price) ? 0.0 : parseFloat(values.price) * 100,
        languageCode: lang,
      }));
    }, [customProductId, updateParams, values, lang]);

    const navigateToProjectConfigurator = useCallback(() => {
      Scrivito.navigateTo(getHomepage()?.get("projectConfiguratorPage"), {
        projectId,
      });
    }, [projectId]);

    const handleNavigateBackToProject = useCallback(async () => {
      if (isUpdateButtonActive && customProductId) {
        showConfiguratorPopup(navigateToProjectConfigurator);
      } else {
        navigateToProjectConfigurator();
      }
    }, [
      isUpdateButtonActive,
      navigateToProjectConfigurator,
      showConfiguratorPopup,
      customProductId,
    ]);

    const handleAddToProject = useCallback(async () => {
      const errors = await validateForm();
      if (Object.keys(errors).length === 0) {
        NiceModal.show(ProductAddingModal, {
          requestParams: {
            productName: values.title,
            kind: NodeKinds.CUSTOM_PRODUCT,
            productDescription: values.description,
            orderCode: null,
            priceGroup: values.priceGroup,
            price: isNaN(values.price) ? 0.0 : parseFloat(values.price) * 100,
            presentation: values.ordercode,
          },
        });
      }
    }, [
      values.description,
      values.ordercode,
      values.price,
      values.priceGroup,
      values.title,
      validateForm,
    ]);

    const headerText =
      typeof translate("CUSTOM_ITEM") === "string"
        ? translate("CUSTOM_ITEM").toUpperCase()
        : translate("CUSTOM_ITEM");

    return (
      <div className="d-flex flex-row flex-shrink-1 w-100 justify-content-between mb-4">
        <div className="d-flex gap-2 align-items-center">
          <h5>{headerText}</h5>
        </div>
        <div className="d-flex mb-2_5">
          <Button
            data-testid="product-configurator-header_back-to-project-structure-btn"
            className="ms-2"
            onClick={handleNavigateBackToProject}
            disabled={!projectId}
          >
            {translate("PRODUCT_CONFIGURATOR_BACK_TO_PROJECT_STRUCTURE")}
          </Button>
          <Button
            color="primary"
            className="ms-2"
            data-testid="custom_item_add_to_project"
            onClick={handleAddToProject}
            type="submit"
          >
            {translate("ADD_TO_PROJECT")}
          </Button>
        </div>
      </div>
    );
  },
);
